import Phaser from 'phaser';
import type { Snapshot } from '../../core/run';
import type { Manifest } from '../assets';
import { resolveAtlasClip } from './atlasClip';
import { lerpEntity } from './interpolate';

// Les planches sont à `scale` fois la taille logique ; ce facteur global ramène la taille logique à l'écran (monde 1280x720).
export const SPRITE_ZOOM = 2.5;

// Dernier animTime vu par vue : s'il diminue, le coeur a relancé l'animation (attack/hit consécutifs).
const lastAnimTime = new WeakMap<object, number>();

type SnapshotEntity = Snapshot['entities'][number];

export interface EntityView { body: Phaser.GameObjects.Sprite | Phaser.GameObjects.Arc; ring: Phaser.GameObjects.Arc | null; }
export type Views = Map<number, EntityView>;

const PROJECTILE_COLORS: Record<string, number> = {
  aiguille: 0xffa040, 'rayon-obscur': 0xffa040, 'poussiere-temporelle': 0xffa040, sablier: 0xffa040,
  gelure: 0x7fd87f, fletrissement: 0x7fd87f,
};

function fallbackColor(e: SnapshotEntity): number {
  if (e.kind === 'projectile') return PROJECTILE_COLORS[e.sprite] ?? 0xcccccc;
  if (e.kind === 'pet') return 0xffa040;
  if (e.kind === 'hazard') return 0x5bb24a;
  return e.kind === 'pickup' ? 0xe8c44a : 0x3f7fbf;
}

export function destroyViews(views: Views): void {
  for (const v of views.values()) { v.body.destroy(); v.ring?.destroy(); }
  views.clear();
}

export function syncEntities(scene: Phaser.Scene, snap: Snapshot, prev: Snapshot | null, alpha: number, views: Views, manifest: Manifest): void {
  const prevById = new Map<number, SnapshotEntity>();
  if (prev) for (const p of prev.entities) prevById.set(p.id, p);
  const seen = new Set<number>();
  for (const e of snap.entities) {
    seen.add(e.id);
    let v = views.get(e.id);
    const atlas = resolveAtlasClip(manifest, e, (key) => scene.textures.exists(key) && scene.anims.exists(key));
    const animKey = atlas ? atlas.key : manifest.sheets[`${e.sprite}-${e.anim}`] ? `${e.sprite}-${e.anim}` : manifest.sheets[`${e.sprite}-idle`] ? `${e.sprite}-idle` : null;
    const pos = lerpEntity(prevById.get(e.id), e, alpha);
    if (!v) {
      let body: EntityView['body'];
      if (atlas) body = scene.add.sprite(pos.x, pos.y, atlas.key).setScale(atlas.scale); // pas de setOrigin : le pivot de l'atlas (pieds) est appliqué à chaque frame
      else if (animKey) { const s = scene.add.sprite(pos.x, pos.y, animKey); const d = manifest.sheets[animKey]!; s.setOrigin(d.anchorX, d.anchorY); s.setScale(SPRITE_ZOOM / d.scale); body = s; }
      else if (e.kind === 'hazard') body = scene.add.circle(pos.x, pos.y, e.radius, fallbackColor(e), 0.22).setStrokeStyle(2, 0x3f8f2f, 0.6); // ronces : disque translucide posé au sol, sans sprite
      else body = scene.add.circle(pos.x, pos.y, e.radius, fallbackColor(e));
      v = { body, ring: null };
      views.set(e.id, v);
    }
    const body = v.body;
    body.setPosition(pos.x, pos.y);
    body.setDepth(e.kind === 'pickup' ? e.y - 1 : e.kind === 'hazard' ? e.y - 3 : e.y);
    if (e.telegraph && !v.ring) v.ring = scene.add.circle(pos.x, pos.y, e.radius + 14, 0xc8322b, 0.45).setStrokeStyle(3, 0xff4a3a, 0.9).setDepth(e.y - 2);
    if (v.ring) v.ring.setPosition(pos.x, pos.y).setDepth(e.y - 2).setVisible(e.telegraph);
    if (body instanceof Phaser.GameObjects.Sprite && animKey) {
      const prevTime = lastAnimTime.get(body);
      const restarted = prevTime !== undefined && e.animTime < prevTime;
      lastAnimTime.set(body, e.animTime);
      if (body.anims.currentAnim?.key !== animKey || restarted) {
        // planches sheets : chaque clip a sa taille de frame et son ancre, réappliquées à chaque changement
        if (!atlas) { const d = manifest.sheets[animKey]!; body.setOrigin(d.anchorX, d.anchorY); }
        body.play(animKey, !restarted);
      }
      body.setFlipX(atlas ? atlas.flipX : e.facingX < 0);
      if (e.tint === 'slow') body.setTint(0x66aaff); else if (e.tint === 'poison') body.setTint(0x9be36b); else body.clearTint();
      if (!e.alive) body.setAlpha(Math.max(0.2, 1 - e.animTime));
    }
  }
  for (const [id, v] of views) if (!seen.has(id)) { v.body.destroy(); v.ring?.destroy(); views.delete(id); }
}
